import React, { useState, useRef, useEffect } from 'react';
import { Grid, Typography, TextField, Select, MenuItem, Button, Paper, Box, LinearProgress, Switch, FormControlLabel } from '@mui/material';

function App() {
  const [name, setName] = useState('');
  const [job, setJob] = useState('');
  const [inputText, setInputText] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [isImageGenerated, setIsImageGenerated] = useState(false); // New state to track if image is generated
  const [gender, setGender] = useState('Mann');
  const [language, setLanguage] = useState('EN'); // New state for language toggle
  const [personality, setPersonality] = useState('Friendly');
  const [artStyle, setArtStyle] = useState('Cartoon Style');
  const [bottoms, setBottoms] = useState('');
  const [tops, setTops] = useState('');
  const [hats, setHats] = useState('');
  const [bottomsColor, setBottomsColor] = useState('Red');
  const [topsColor, setTopsColor] = useState('Red');
  const [hatsColor, setHatsColor] = useState('Red');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState('');
  const [progress, setProgress] = useState(0);

  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const chatHistoryRef = useRef(null); // For auto-scrolling chat
  let streamRef = null;

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        streamRef = stream;
        console.log('Microphone access granted.');
      })
      .catch((error) => {
        console.error('Microphone access error:', error);
      });
  }, []);

  const startFixedTimeRecording = async () => {
    setIsRecording(true);
    setRecordingStatus(language === 'EN' ? 'Recording...' : 'aufnahme...');
    setProgress(0);

    try {
      if (!streamRef) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef = stream;
      }

      mediaRecorderRef.current = new MediaRecorder(streamRef);
      const audioChunks = [];
      mediaRecorderRef.current.ondataavailable = (event) => audioChunks.push(event.data);

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
        handleSendAudio(audioBlob);
        setIsRecording(false);
        setRecordingStatus('');
        setProgress(0); // Reset progress after recording
      };

      mediaRecorderRef.current.start(1000);

      setTimeout(() => {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      }, 5000); // 5 seconds

      // Smooth progress bar for 5 seconds recording
      timerRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          const nextProgress = prevProgress + 2; // Smooth progress in steps
          if (nextProgress >= 100) {
            clearInterval(timerRef.current);
            setProgress(100); // Ensure the bar reaches full progress before stopping
            return 100;
          }
          return nextProgress;
        });
      }, 90); // Updates every 90ms to ensure bar aligns with the 5s duration
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setRecordingStatus(language === 'EN' ? 'Error starting recording' : 'Fehler beim Starten der Aufnahme');
    }
  };

  const handleSendAudio = async (audioBlob) => {
    if (!audioBlob) return;

    const formData = new FormData();
    formData.append('audio', audioBlob);

    try {
      const response = await fetch('/api/whisper', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      setInputText(data.transcription);
      handleSendText(data.transcription);
    } catch (error) {
      console.error('Error transcribing audio:', error);
    }
  };

  const handleSendText = async (text) => {
    if (text.trim() === '') return;

    setChatHistory([...chatHistory, { sender: language === 'EN' ? 'You' : 'Du', message: text }]);
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, job, personality, gender, message: text, artStyle }),
      });

      const data = await response.json();
      const assistantMessage = data.reply;
      const assistantName = `${name || (language === 'EN' ? 'Assistant' : 'Assistent')}, ${language === 'EN' ? 'the' : (gender === 'Mann' ? 'der' : 'die')} ${job || ''}`;

      setChatHistory([...chatHistory, { sender: language === 'EN' ? 'You' : 'Du', message: text }, { sender: assistantName, message: assistantMessage }]);
      playTTS(assistantMessage, gender);

      // Auto-scroll chat history
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    } catch (error) {
      console.error('Error:', error);
      setChatHistory([...chatHistory, { sender: 'Assistent', message: 'Es gab einen Fehler.' }]);
    }

    setInputText('');
    setIsLoading(false);
  };

  const playTTS = async (text, gender) => {
    try {
      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, gender }),
      });

      const data = await response.json();
      const audioUrl = data.audioUrl;

      if (audioUrl) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
      } else {
        console.error('No audio URL returned');
      }
    } catch (error) {
      console.error('Error with TTS:', error);
    }
  };

  const playMostRecentTTS = async () => {
    try {
      const response = await fetch('/api/recent-tts');
      if (!response.ok) {
        console.error('No recent TTS file found.');
        return;
      }
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch((error) => console.error('Error playing recent TTS:', error));
    } catch (error) {
      console.error('Error fetching recent TTS:', error);
    }
  };

  const handleGenerateImage = async () => {
    if (name.trim() === '' && job.trim() === '' && bottoms.trim() === '' && tops.trim() === '' && hats.trim() === '') return;
    setIsImageLoading(true);

    try {
      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, job, gender, personality, bottoms, tops, hats, bottomsColor, topsColor, hatsColor, artStyle, language }),
      });

      if (!response.ok) throw new Error('Error generating image');

      const data = await response.json();
      setGeneratedImageUrl(data.imageUrl);
      setIsImageGenerated(true);  // Set to true when image is generated
    } catch (error) {
      console.error('Error generating image:', error);
    }
    setIsImageLoading(false);
  };

  const toggleLanguage = () => {
    setLanguage((prevLanguage) => {
      const newLanguage = prevLanguage === 'EN' ? 'DE' : 'EN';
  
      if (newLanguage === 'EN') {
        setPersonality('Friendly');
        setArtStyle('Cartoon Style');
        setBottomsColor('Red');
        setTopsColor('Red');
        setHatsColor('Red');
      } else {
        setPersonality('Freundlich');
        setArtStyle('Cartoon-Stil');
        setBottomsColor('Rot');
        setTopsColor('Rot');
        setHatsColor('Rot');
      }
  
      return newLanguage;
    });
  };  

  return (
    <Grid container spacing={4} style={{ padding: '20px' }} alignItems="flex-start">
      <Grid item xs={12} style={{ textAlign: 'center', marginBottom: '20px' }}>
        <Typography variant="h3" component="h1" style={{ fontWeight: 'bold' }}>{language === 'EN' ? 'Dream Factory' : 'Traumfabrik'}</Typography>
        <Box display="flex" justifyContent="center" alignItems="center" marginBottom="20px">
          <Button
            variant={language === 'EN' ? 'contained' : 'outlined'}
            onClick={() => setLanguage('EN')}
            style={{ marginRight: '10px' }}
            size="small" 
          >
            English
          </Button>
          <Button
            variant={language === 'DE' ? 'contained' : 'outlined'}
            onClick={() => setLanguage('DE')}
            size="small" 
          >
            Deutsch
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12} md={4} style={{ paddingRight: '10px' }}>
        <Paper elevation={3} 
          style={{ 
            height: 'auto', 
            minHeight: '400px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            '@media (min-width: 600px)': {
              height: '512px'
            }
          }}
        >
          {isImageLoading ? (
            <Typography>{language === 'EN' ? 'Generating image...' : 'Bild wird generiert...'}</Typography>
          ) : generatedImageUrl ? (
            <img src={generatedImageUrl} alt="Generated" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', imageRendering: 'auto' }} />
          ) : (
            <Typography variant="body1" color="textSecondary">{language === 'EN' ? 'Image will appear here' : 'Bild erscheint hier'}</Typography>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={4} style={{ paddingLeft: '10px', paddingRight: '10px' }}>
        <Box component="form" noValidate autoComplete="off">
          <TextField label={language === 'EN' ? 'Gender' : 'Geschlecht'} select value={gender} onChange={(e) => setGender(e.target.value)} fullWidth margin="normal" variant="outlined">
            <MenuItem value="Mann">{language === 'EN' ? 'Male' : 'Mann'}</MenuItem>
            <MenuItem value="Frau">{language === 'EN' ? 'Female' : 'Frau'}</MenuItem>
          </TextField>

          <TextField label={language === 'EN' ? 'Name' : 'Name'} variant="outlined" value={name} onChange={(e) => setName(e.target.value)} fullWidth margin="normal" />
          <TextField label={language === 'EN' ? 'Job' : 'Beruf'} variant="outlined" value={job} onChange={(e) => setJob(e.target.value)} fullWidth margin="normal" />

          <TextField label={language === 'EN' ? 'Personality' : 'Persönlichkeit'} select value={personality} onChange={(e) => setPersonality(e.target.value)} fullWidth margin="normal" variant="outlined">
            {(language === 'EN' ? 
              ['Friendly', 'Evil', 'Heroic', 'Cheeky', 'Lazy', 'Clumsy', 'Daydreamer'] :
              ['Freundlich', 'Böse', 'Heldenhaft', 'Frech', 'Faul', 'Tollpatschig', 'Tagträumer'])
            .map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>

          <TextField label={language === 'EN' ? 'Art Style' : 'Zeichenstil'} select value={artStyle} onChange={(e) => setArtStyle(e.target.value)} fullWidth margin="normal" variant="outlined">
            {(language === 'EN' ?
              ['Cartoon Style', 'Disney Style', 'Watercolor', 'Pencil Drawing', 'Pixar Style', 'Emoji Style', 'Pixel Style', 'Photorealistic Style'] :
              ['Cartoon-Stil', 'Disney-Stil', 'Wasserfarben', 'Bleistift-Zeichnung', 'Pixar-Stil', 'Emoji-Stil', 'Pixel-Stil', 'Fotorealistischer Stil'])
            .map((style) => (
              <MenuItem key={style} value={style}>{style}</MenuItem>
            ))}
          </TextField>

          <Typography variant="h6">{language === 'EN' ? 'Clothing:' : 'Kleidung:'}</Typography>
          <TextField label={language === 'EN' ? 'Bottoms' : 'Hosen'} variant="outlined" value={bottoms} onChange={(e) => setBottoms(e.target.value)} fullWidth margin="normal" placeholder={language === 'EN' ? 'e.g., clown pants' : 'z.B. Clownshosen'} />
          <TextField label={language === 'EN' ? 'Bottoms Color' : 'Hosen Farbe'} select value={bottomsColor} onChange={(e) => setBottomsColor(e.target.value)} fullWidth margin="normal" variant="outlined">
            {(language === 'EN' ?
              ['Red', 'Blue', 'Green', 'Yellow', 'Black', 'White', 'Purple', 'Pink', 'Gray'] :
              ['Rot', 'Blau', 'Grün', 'Gelb', 'Schwarz', 'Weiß', 'Lila', 'Rosa', 'Grau'])
            .map((color, index) => (
              <MenuItem key={index} value={color}>{color}</MenuItem>
            ))}
          </TextField>

          <TextField label={language === 'EN' ? 'Tops' : 'Oberteile'} variant="outlined" value={tops} onChange={(e) => setTops(e.target.value)} fullWidth margin="normal" placeholder={language === 'EN' ? 'e.g., pirate shirt' : 'z.B. Piratenhemd'} />
          <TextField label={language === 'EN' ? 'Tops Color' : 'Oberteil Farbe'} select value={topsColor} onChange={(e) => setTopsColor(e.target.value)} fullWidth margin="normal" variant="outlined">
            {(language === 'EN' ?
              ['Red', 'Blue', 'Green', 'Yellow', 'Black', 'White', 'Purple', 'Pink', 'Gray'] :
              ['Rot', 'Blau', 'Grün', 'Gelb', 'Schwarz', 'Weiß', 'Lila', 'Rosa', 'Grau'])
            .map((color, index) => (
              <MenuItem key={index} value={color}>{color}</MenuItem>
            ))}
          </TextField>

          <TextField label={language === 'EN' ? 'Hats' : 'Hüte'} variant="outlined" value={hats} onChange={(e) => setHats(e.target.value)} fullWidth margin="normal" placeholder={language === 'EN' ? 'e.g., pirate hat' : 'z.B. Piratenhut'} />
          <TextField label={language === 'EN' ? 'Hat Color' : 'Hut Farbe'} select value={hatsColor} onChange={(e) => setHatsColor(e.target.value)} fullWidth margin="normal" variant="outlined">
            {(language === 'EN' ?
              ['Red', 'Blue', 'Green', 'Yellow', 'Black', 'White', 'Purple', 'Pink', 'Gray'] :
              ['Rot', 'Blau', 'Grün', 'Gelb', 'Schwarz', 'Weiß', 'Lila', 'Rosa', 'Grau'])
            .map((color, index) => (
              <MenuItem key={index} value={color}>{color}</MenuItem>
            ))}
          </TextField>

          <Button variant="contained" color="primary" onClick={handleGenerateImage} fullWidth style={{ marginTop: '20px' }}>{language === 'EN' ? 'Generate Image' : 'Bild Generieren'}</Button>
        </Box>
      </Grid>

      <Grid item xs={12} md={4} style={{ paddingLeft: '10px' }}>
        <Typography variant="h6">{language === 'EN' ? 'Chat History:' : 'Chatverlauf:'}</Typography>
        <Paper ref={chatHistoryRef} style={{ border: '1px solid #ccc', padding: '10px', maxHeight: '400px', overflowY: 'auto', backgroundColor: '#f9f9f9', flexGrow: 1 }}>
          {chatHistory.map((message, index) => (
            <Box key={index} sx={{ marginBottom: '15px', wordBreak: 'break-word', backgroundColor: message.sender === (language === 'EN' ? 'You' : 'Du') ? '#ffe0b2' : '#cfe8fc', padding: '10px', borderRadius: '5px' }}>
              <Typography variant="body1"><strong>{message.sender}:</strong> {message.message}</Typography>
            </Box>
          ))}
          {isLoading && <Typography>{language === 'EN' ? 'Loading...' : 'Wird geladen...'}</Typography>}
        </Paper>

        <TextField multiline rows={4} value={inputText} onChange={(e) => setInputText(e.target.value)} fullWidth margin="normal" />

        <Grid container spacing={2} style={{ marginTop: '10px' }}>
          <Grid item xs={6}>
            <Button variant="contained" color={isRecording ? "error" : "success"} fullWidth onClick={startFixedTimeRecording} disabled={!isImageGenerated || isRecording}>
              {isRecording ? (language === 'EN' ? 'Recording...' : 'aufnahme...') : (language === 'EN' ? '5s Recording' : '5s Aufnahme')}
            </Button>
          </Grid>

          <Grid item xs={6}>
            <Button variant="contained" color="primary" fullWidth onClick={() => handleSendText(inputText)} disabled={!isImageGenerated}>
              {language === 'EN' ? 'Send' : 'Senden'}
            </Button>
          </Grid>

          <Grid item xs={6}>
            <Button variant="contained" color="primary" fullWidth onClick={playMostRecentTTS} disabled={!isImageGenerated}>
              {language === 'EN' ? 'Play Last Response' : 'Antwort nochmal abspielen'}
            </Button>
          </Grid>
        </Grid>

        {isRecording && <LinearProgress variant="determinate" value={progress} style={{ marginTop: '10px' }} />}

        <audio ref={audioRef} />
      </Grid>
    </Grid>
  );
}

export default App;
